import React from "react";
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  Paper,
  Typography,
} from "@mui/material";
import {
  CheckCircle,
  RadioButtonUnchecked,
  Tune,
  HourglassEmpty,
  ErrorOutline,
} from "@mui/icons-material";

/**
 * The listing journey, in the order it actually happens:
 *
 *   add property → apply settings → admin approval → live → rented
 *
 * Owners kept getting stuck between the first two steps because the only hint
 * was a permanent toast with a button inside it. Both pieces here explain the
 * same pipeline: the dialog right after a save, the banner every time the
 * owner opens My Properties until nothing is outstanding.
 */

/* --- shared state read off the flat list -------------------------------- */

export const setupState = (flats = []) => {
  const list = Array.isArray(flats) ? flats : [];
  const isRented = (f) => Number(f.is_rent) === 1;

  return {
    needsSettings: list.filter(
      (f) => !f.is_property_settings && !f.is_rejected && !isRented(f)
    ),
    awaitingApproval: list.filter(
      (f) => f.is_property_settings && !f.is_approved && !f.is_rejected
    ),
    rejected: list.filter((f) => f.is_rejected),
  };
};

/* --- one row of the three-step explainer -------------------------------- */

function Step({ state, title, caption }) {
  const done = state === "done";
  const current = state === "current";

  const Icon = done ? CheckCircle : current ? Tune : RadioButtonUnchecked;

  return (
    <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start", py: 1 }}>
      <Icon
        sx={{
          fontSize: 22,
          mt: "2px",
          flexShrink: 0,
          color: done
            ? "success.main"
            : current
            ? "var(--owner-primary)"
            : "#cbd5e1",
        }}
      />
      <Box>
        <Typography
          sx={{
            fontWeight: current || done ? 700 : 600,
            color: current || done ? "text.primary" : "text.secondary",
          }}
        >
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
      </Box>
    </Box>
  );
}

/* --- shown once, right after a property is created ---------------------- */

export function PropertyAddedDialog({ open, onClose, onApplySettings }) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <Box
        sx={{
          px: 3,
          pt: 3,
          pb: 2,
          display: "flex",
          gap: 1.5,
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
            bgcolor: "color-mix(in srgb, var(--owner-primary) 12%, transparent)",
            color: "var(--owner-primary)",
          }}
        >
          <CheckCircle />
        </Box>
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Property saved — but not live yet
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Right now no tenant can see it.
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 0 }}>
        <Box
          sx={{
            p: 1.75,
            mb: 1,
            borderRadius: 2,
            bgcolor: "#fffbeb",
            border: "1px solid #fcd34d",
          }}
        >
          <Typography variant="body2" sx={{ color: "#92400e", fontWeight: 600 }}>
            Until you apply a setting, this property stays hidden from everyone
            and is never sent to the admin. Approval only starts after that.
          </Typography>
        </Box>

        <Step
          state="done"
          title="Property details added"
          caption="Photos, rent and terms are saved."
        />
        <Step
          state="current"
          title="Apply settings"
          caption="Your termination and violation rules — this is what submits the property for approval."
        />
        <Step
          state="todo"
          title="Admin approval within 24 hours"
          caption="We review it and notify you. Once approved it goes live and tenants can see and book it."
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, pt: 0, gap: 1 }}>
        <Button onClick={onClose} sx={{ color: "text.secondary" }}>
          Later
        </Button>
        <Button variant="contained" onClick={onApplySettings}>
          Apply settings
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/* --- standing reminder on the My Properties page ------------------------ */

export function PropertyPipelineBanner({ flats, onApplySettings, onViewRejected }) {
  const { needsSettings, awaitingApproval, rejected } = setupState(flats);

  if (rejected.length > 0) {
    return (
      <Banner
        tone="error"
        icon={<ErrorOutline />}
        title={
          rejected.length === 1
            ? "1 property was rejected"
            : `${rejected.length} properties were rejected`
        }
        caption="Open the property to read the reason, fix it and send it back for approval."
        action={
          onViewRejected && (
            <Button variant="outlined" color="error" onClick={onViewRejected}>
              See why
            </Button>
          )
        }
      />
    );
  }

  if (needsSettings.length > 0) {
    return (
      <Banner
        tone="primary"
        icon={<Tune />}
        title={
          needsSettings.length === 1
            ? "1 property is waiting for its settings"
            : `${needsSettings.length} properties are waiting for their settings`
        }
        caption="Apply your termination and violation rules — that is what sends a property to the admin for approval."
        chips={needsSettings.slice(0, 3).map((f) => f.flatName)}
        extra={needsSettings.length > 3 ? needsSettings.length - 3 : 0}
        action={
          <Button variant="contained" onClick={onApplySettings}>
            Apply settings
          </Button>
        }
      />
    );
  }

  if (awaitingApproval.length > 0) {
    return (
      <Banner
        tone="warning"
        icon={<HourglassEmpty />}
        title={
          awaitingApproval.length === 1
            ? "1 property is with the admin"
            : `${awaitingApproval.length} properties are with the admin`
        }
        caption="Everything is submitted. You'll get a notification the moment it is approved and visible to tenants."
      />
    );
  }

  return null;
}

const TONES = {
  primary: {
    border: "color-mix(in srgb, var(--owner-primary) 35%, transparent)",
    bg: "color-mix(in srgb, var(--owner-primary) 6%, #fff)",
    icon: "var(--owner-primary)",
  },
  warning: { border: "#fcd34d", bg: "#fffbeb", icon: "#b45309" },
  error: { border: "#fca5a5", bg: "#fef2f2", icon: "#b91c1c" },
};

function Banner({ tone, icon, title, caption, chips = [], extra = 0, action }) {
  const palette = TONES[tone] || TONES.primary;

  return (
    <Paper
      elevation={0}
      sx={{
        p: { xs: 2, md: 2.5 },
        mb: 3,
        borderRadius: 3,
        border: "1px solid",
        borderColor: palette.border,
        bgcolor: palette.bg,
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
      }}
    >
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: 2,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          color: palette.icon,
          bgcolor: "rgba(255,255,255,0.75)",
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 220 }}>
        <Typography fontWeight={700}>{title}</Typography>
        <Typography variant="body2" color="text.secondary">
          {caption}
        </Typography>
        {chips.length > 0 && (
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mt: 1 }}>
            {chips.map((label, i) => (
              <Chip key={i} size="small" variant="outlined" label={label} />
            ))}
            {extra > 0 && (
              <Chip size="small" variant="outlined" label={`+${extra} more`} />
            )}
          </Box>
        )}
      </Box>

      {action}
    </Paper>
  );
}

export default PropertyPipelineBanner;
